// UpdateMenuModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { updateMenu } from '../../../../../../../../crud/menu_api/menus';
import { toast } from 'react-toastify';
import { HiOutlineXMark } from 'react-icons/hi2';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import ScheduleEditor from './components/ScheduleEditor/ScheduleEditor';
import { MenuSchedule } from '../../../../types/menuSchedule.types';
import { MenuCategoryNode, MenuType } from '../../../../types/menu.types';
import './UpdateMenuModal.scss';

interface UpdateModalProps {
  show: boolean;
  handleClose: () => void;
  menu: MenuType;
  menus: MenuType[];
  setMenus: (menus: MenuType[]) => void;
  setSelectedMenu: (menu: MenuType | null) => void;
  refreshMenus?: () => void; // NEW: Function to refresh menus
}

const UpdateMenuModal: React.FC<UpdateModalProps> = ({
  show,
  handleClose,
  menu,
  menus,
  setMenus,
  setSelectedMenu,
  refreshMenus,
}) => {
  const [menuForm, setMenuForm] = useState({
    menu_name: menu.menu_name,
    menu_description: menu.menu_description,
    local_id: 0,
  });
  const [menuSchedule, setMenuSchedule] = useState<MenuSchedule | null>(menu.schedule || null);
  const [hasSchedule, setHasSchedule] = useState<boolean>(menu.schedule !== null && menu.schedule !== undefined);
  const [initialHasSchedule, setInitialHasSchedule] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    const userContext = localStorage.getItem('user');
    const localId = userContext ? JSON.parse(userContext).local_id : 0;
    if (show) {
      setMenuForm({
        menu_name: menu.menu_name,
        menu_description: menu.menu_description,
        local_id: localId,
      });
      setMenuSchedule(menu.schedule || null);
      const scheduleExists = menu.schedule !== null && menu.schedule !== undefined;
      setHasSchedule(scheduleExists);
      setInitialHasSchedule(scheduleExists);
      setHasUnsavedChanges(false);
    }
  }, [show, menu]);

  // Detect unsaved changes and warn before leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (show && hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [show, hasUnsavedChanges]);

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMenuForm({
      ...menuForm,
      [event.target.name]: event.target.value,
    });
    setHasUnsavedChanges(true);
  };

  const handleUpdateMenu = async (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    event.preventDefault();
    
    // Build the update payload
    const updatePayload: any = {
      ...menuForm,
    };

    // If schedule is enabled include it, otherwise explicitly send null
    if (hasSchedule && menuSchedule) {
      updatePayload.schedule = {
        weekly_schedule: menuSchedule.weekly_schedule,
        exceptions: menuSchedule.exceptions || []
      };
    } else {
      // Explicitly request removal/disable of schedule on the backend
      updatePayload.schedule = null;
    }

    updateMenu(Number(menu.menu_id), updatePayload)
      .then((response) => {
        toast.success('Menú actualizado exitosamente');
        
        // Backend doesn't return updated menu data, so we build it from our form state
        const updatedMenu: MenuType = {
          menu_id: menu.menu_id,
          menu_name: menuForm.menu_name,
          menu_description: menuForm.menu_description,
          state: menu.state,
          // Use local state for schedule since backend doesn't return it
          schedule: hasSchedule ? menuSchedule : null,
          categories: menu.categories,
        };
        
        const updatedMenus = menus.map((m) =>
          m.menu_id === updatedMenu.menu_id ? updatedMenu : m
        );
        setMenus(updatedMenus);
        setSelectedMenu(updatedMenu);
        
        // Refresh menus from backend to ensure data is in sync
        if (refreshMenus) {
          refreshMenus();
        }
        
        setHasUnsavedChanges(false);
        handleClose();
      })
      .catch((error) => {
        console.error('Error updating menu:', error);
        toast.error('Error al actualizar el menú');
      }).finally(() => setLoading(false));
  };

  const handleCloseWithConfirm = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Tienes cambios sin guardar. ¿Estás seguro de que deseas cerrar?')) {
        setHasUnsavedChanges(false);
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  return (
    <Modal show={show} onHide={handleCloseWithConfirm} className="mp-modal update-menu-modal" centered>
      <div className="mp-modal-header">
        <h2 className="mp-modal-title">Actualizar Menú</h2>
        <button className="mp-modal-close" onClick={handleCloseWithConfirm}>
          <HiOutlineXMark size={18} />
        </button>
      </div>
      <div className="mp-modal-body">
        {loading ? (
          <div className="mini-loading-container">
            <MiniLoadingSpinner />
          </div>
        ) : (
          <form onSubmit={handleUpdateMenu}>
            <div className="form-content-scrollable">
              <div className="mp-form-group">
                <label className="mp-form-label">Nombre del Menú</label>
                <input
                  className="mp-form-input"
                  type="text"
                  name="menu_name"
                  value={menuForm.menu_name}
                  onChange={handleFormChange}
                  placeholder="Ingresa el nombre del menú"
                  required
                />
              </div>

              <div className="mp-form-group">
                <label className="mp-form-label">Descripción</label>
                <input
                  className="mp-form-input"
                  type="text"
                  name="menu_description"
                  value={menuForm.menu_description}
                  onChange={handleFormChange}
                  placeholder="Descripción opcional del menú..."
                />
              </div>

              {/* Schedule Toggle */}
              <div className="schedule-toggle-row">
                <label className="mp-form-label">Configurar Horario</label>
                <div className="toggle-container">
                  <label className="menu-toggle-switch">
                    <input
                      type="checkbox"
                      checked={hasSchedule}
                      onChange={(e) => {
                        setHasSchedule(e.target.checked);
                        setHasUnsavedChanges(true);
                        if (!e.target.checked) {
                          setMenuSchedule(null);
                        } else if (!menuSchedule) {
                          setMenuSchedule({
                            weekly_schedule: {
                              monday: [{ open: '09:00', close: '22:00' }],
                              tuesday: [{ open: '09:00', close: '22:00' }],
                              wednesday: [{ open: '09:00', close: '22:00' }],
                              thursday: [{ open: '09:00', close: '22:00' }],
                              friday: [{ open: '09:00', close: '22:00' }],
                              saturday: [{ open: '09:00', close: '22:00' }],
                              sunday: [],
                            },
                            exceptions: [],
                          });
                        }
                      }}
                    />
                    <span className="menu-toggle-slider"></span>
                  </label>
                  <span className="toggle-label-text">
                    {hasSchedule ? 'Horario configurado' : 'Sin horario (disponible siempre)'}
                  </span>
                </div>
              </div>

              {/* Warning Banner when disabling schedule */}
              {initialHasSchedule && !hasSchedule && (
                <div className="schedule-warning-banner">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-content">
                    <strong>Atención:</strong> Al desactivar el horario, se eliminará la configuración actual del horario del menú. 
                    El menú estará disponible en todo momento una vez que actualices.
                  </div>
                </div>
              )}

              {/* Schedule Editor */}
              {hasSchedule && (
                <ScheduleEditor
                  schedule={menuSchedule}
                  onChange={(newSchedule) => {
                    setMenuSchedule(newSchedule);
                    setHasUnsavedChanges(true);
                  }}
                />
              )}
            </div>
            <div className="mp-modal-footer">
              <button type="button" className="mp-modal-btn-cancel" onClick={handleCloseWithConfirm}>
                Cancelar
              </button>
              <button type="submit" className="mp-modal-btn-submit" disabled={loading}>
                Actualizar Menú
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default UpdateMenuModal;
